import {ChangeDetectionStrategy, Component, input, output, signal, viewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {catchError, EMPTY, finalize, forkJoin, map, switchMap} from 'rxjs';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeBannerModule} from 'se-fe-banner';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldQuantityModule} from 'se-fe-form-field-quantity';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeModalButton, SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {SeFePositionService} from 'se-fe-position';
import {SeFeTableDataSource, SeFeTableModule} from 'se-fe-table';
import {SeFeToastService} from 'se-fe-toast';
import {SeFeTooltipModule} from 'se-fe-tooltip';
import {InventoryVariation} from '../../../../../models/clare';
import {ProductApiService} from '../../../../../services/product/product-api.service';

type InventoryForm = Record<string, FormControl<number | null>>;

@Component({
  selector: 'product-inventory-modal',
  imports: [
    ReactiveFormsModule,
    SeFeAlertModule,
    SeFeBannerModule,
    SeFeButtonModule,
    SeFeFormFieldModule,
    SeFeFormFieldQuantityModule,
    SeFeIconModule,
    SeFeModalModule,
    SeFeTableModule,
    SeFeTooltipModule,
  ],
  providers: [SeFePositionService],
  templateUrl: './product-inventory-modal.component.html',
  styleUrls: ['./product-inventory-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductInventoryModalComponent {
  readonly inventoryUpdated = output<InventoryVariation[]>();
  readonly inventoryModal = viewChild.required<SeFeModalComponent>('inventoryModal');
  readonly discardChangesModal = viewChild.required<SeFeModalComponent>('discardChangesModal');
  readonly inventoryConflictModal = viewChild.required<SeFeModalComponent>('inventoryConflictModal');

  productId = input.required<string | number>();
  variants = input.required<InventoryVariation[]>();
  showTrigger = input(true);

  form = new FormGroup<InventoryForm>({});
  saving = signal(false);
  saveError = signal<string | null>(null);

  readonly tableColumns = ['name', 'quantityAvailable', 'inventory'];
  readonly inventoryConfig = {label: 'Inventory limit', hiddenLabel: true};
  readonly discardChangesModalButtons: SeFeModalButton[] = [
    {
      cancel: true,
      label: 'Keep editing',
      action: () => {
        this.discardChangesModal().cancelSpinner();
        this.discardChangesModal().close();
      },
    },
    {
      destructive: true,
      label: 'Discard changes',
      action: () => this.discardChanges(),
    },
  ];
  readonly inventoryConflictModalButtons: SeFeModalButton[] = [
    {
      cancel: true,
      label: 'Cancel',
      action: () => {
        this.inventoryConflictModal().cancelSpinner();
        this.inventoryConflictModal().close();
      },
    },
    {
      secondary: true,
      label: 'Set quantity to entered value',
      action: () => this.resolveInventoryConflict(false),
    },
    {
      secondary: true,
      label: 'Set quantity to adjusted value',
      action: () => this.resolveInventoryConflict(true),
    },
  ];
  // se-fe-form-field-quantity defaults min to 1 and adds Validators.min for that value.
  readonly inventoryMin = Number.MIN_SAFE_INTEGER;
  inventoryDatasource = new SeFeTableDataSource([]);

  private originalInventory = new Map<number, number | null>();
  private latestVariants: InventoryVariation[] | undefined;

  constructor(
    private productApi: ProductApiService,
    private toast: SeFeToastService
  ) {}

  open(trigger: Event | Element): void {
    this.reset();
    this.productApi.getProductVariants(String(this.productId())).subscribe({
      next: variants => {
        this.initializeForm(variants as InventoryVariation[]);
        this.inventoryModal().open(trigger);
      },
      error: () => {
        this.saveError.set('Could not load the current inventory. Please try again.');
        this.inventoryModal().open(trigger);
      },
    });
  }

  save(trigger?: Event | Element): void {
    this.markControlsTouched();
    if (this.form.invalid) return;

    this.saving.set(true);
    this.productApi.getProductVariants(String(this.productId())).subscribe({
      next: latestVariants => {
        this.latestVariants = this.withKnownInventory(latestVariants as InventoryVariation[]);
        if (this.hasInventoryChanged(this.latestVariants)) {
          this.setConflictButtonLabels();
          this.saving.set(false);
          this.inventoryConflictModal().open(trigger);
          return;
        }
        this.saveLatestInventory();
      },
      error: () => {
        this.saving.set(false);
        this.saveError.set('Could not verify the current inventory. Please try again.');
      },
    });
  }

  close(trigger?: HTMLElement | null): void {
    if (this.saving()) return;

    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    activeElement?.blur();
    if (this.form.dirty) {
      this.discardChangesModal().open(trigger ?? activeElement);
      return;
    }

    this.inventoryModal().close();
  }

  reset(): void {
    this.form = new FormGroup<InventoryForm>({});
    this.originalInventory.clear();
    this.latestVariants = undefined;
    this.inventoryDatasource = new SeFeTableDataSource([]);
    this.saveError.set(null);
    this.saving.set(false);
  }

  private initializeForm(variants: InventoryVariation[]): void {
    this.latestVariants = this.withKnownInventory(variants);
    this.inventoryDatasource = new SeFeTableDataSource(this.latestVariants);
    this.latestVariants.forEach(variant => {
      if (variant.id === undefined) return;
      this.originalInventory.set(variant.id, variant.inventory ?? null);
      this.form.addControl(
        String(variant.id),
        new FormControl<number | null>(variant.inventory ?? null, {
          validators: [Validators.pattern(/^-?\d+$/)],
        })
      );
    });
  }

  private resolveInventoryConflict(adjustForConcurrentSales: boolean): void {
    this.inventoryConflictModal().cancelSpinner();
    this.inventoryConflictModal().close();
    this.saveLatestInventory(adjustForConcurrentSales);
  }

  private discardChanges(): void {
    this.discardChangesModal().cancelSpinner();
    this.discardChangesModal().close();
    this.inventoryModal().close();
  }

  private saveLatestInventory(adjustForConcurrentSales = false): void {
    const latestVariants = this.latestVariants!;
    const updates = latestVariants
      .filter(variant => variant.id !== undefined)
      .map(variant => ({variant, inventory: this.inventoryForSave(variant, adjustForConcurrentSales)}))
      .filter(({variant, inventory}) => inventory !== (variant.inventory ?? null));

    if (updates.length === 0) {
      this.saving.set(false);
      this.inventoryModal().close();
      return;
    }

    this.saving.set(true);
    const savedInventory = new Map(
      updates.filter(({variant}) => variant.id !== undefined).map(({variant, inventory}) => [variant.id!, inventory])
    );
    forkJoin(updates.map(({variant, inventory}) => this.productApi.updateProductVariant({id: variant.id!, inventory})))
      .pipe(
        switchMap(() =>
          this.productApi.getProductVariants(String(this.productId())).pipe(
            catchError(() => {
              this.saveError.set('Inventory was saved, but could not be refreshed. Please reopen the modal.');
              return EMPTY;
            }),
            map(fetched => this.applyInventory(fetched as InventoryVariation[], savedInventory))
          )
        ),
        finalize(() => this.saving.set(false))
      )
      .subscribe({
        next: variants => {
          this.inventoryUpdated.emit(variants);
          this.inventoryModal().close();
          this.toast.done('Inventory updated');
        },
        error: () => {
          this.saveError.set('Could not save inventory. Please try again.');
        },
      });
  }

  private withKnownInventory(variants: InventoryVariation[]): InventoryVariation[] {
    const known = new Map(
      this.variants()
        .filter(variant => variant.id !== undefined && variant.inventory !== undefined)
        .map(variant => [variant.id!, variant.inventory ?? null])
    );
    return this.applyInventory(variants, known, {onlyWhenMissing: true});
  }

  private applyInventory(
    variants: InventoryVariation[],
    inventoryById: Map<number, number | null>,
    options?: {onlyWhenMissing?: boolean}
  ): InventoryVariation[] {
    return variants.map(variant => {
      if (variant.id === undefined || !inventoryById.has(variant.id)) return variant;
      if (options?.onlyWhenMissing && variant.inventory !== undefined) return variant;
      return {...variant, inventory: inventoryById.get(variant.id)!};
    });
  }

  private hasInventoryChanged(latestVariants: InventoryVariation[]): boolean {
    return this.conflictingVariants(latestVariants).length > 0;
  }

  private setConflictButtonLabels(): void {
    const conflicts = this.conflictingVariants(this.latestVariants ?? []);
    this.inventoryConflictModalButtons[1].label = `Set quantity to ${this.formatConflictQuantities(conflicts, false)}`;
    this.inventoryConflictModalButtons[2].label = `Set quantity to ${this.formatConflictQuantities(conflicts, true)}`;
  }

  private conflictingVariants(latestVariants: InventoryVariation[]): InventoryVariation[] {
    return latestVariants.filter(variant => {
      if (variant.id === undefined) return false;
      return (variant.inventory ?? null) !== this.originalInventory.get(variant.id);
    });
  }

  private formatConflictQuantities(variants: InventoryVariation[], adjustForConcurrentSales: boolean): string {
    return variants
      .map(variant => {
        const quantity = this.formatInventory(this.inventoryForSave(variant, adjustForConcurrentSales));
        return variants.length > 1 && variant.name ? `${variant.name}: ${quantity}` : quantity;
      })
      .join(', ');
  }

  private formatInventory(inventory: number | null): string {
    return inventory === null ? 'Unlimited' : String(inventory);
  }

  private inventoryFor(variantId: number): number | null {
    const raw = this.form.controls[String(variantId)]?.value;
    return raw === null || raw === undefined ? null : raw;
  }

  private inventoryForSave(variant: InventoryVariation, adjustForConcurrentSales: boolean): number | null {
    const inventory = this.inventoryFor(variant.id!);
    const originalInventory = this.originalInventory.get(variant.id!);
    const latestInventory = variant.inventory ?? null;

    if (
      !adjustForConcurrentSales ||
      inventory === null ||
      originalInventory === null ||
      originalInventory === undefined ||
      latestInventory === null
    ) {
      return inventory;
    }

    const quantitySoldWhileEditing = originalInventory - latestInventory;
    return inventory - quantitySoldWhileEditing;
  }

  private markControlsTouched(): void {
    Object.values(this.form.controls).forEach(control => control.markAsTouched());
  }
}
